import { motion } from 'framer-motion';
import { Phone, CheckCircle, Watch, Shield, Clock, Award } from 'lucide-react';
import { Link } from 'react-router-dom';
import { FAQ } from '../components/FAQ';
import { Contact } from '../components/Contact';

const WHATSAPP_NUMBER = "5521980277322";

const services = [
  "Troca de tela/display",
  "Substituição de bateria",
  "Reparo de botões laterais",
  "Troca de coroa digital",
  "Reparo de sensores (cardíaco, GPS)",
  "Restauração de software",
  "Troca de vedação (waterproof)",
  "Reparo de vibração",
  "Sincronização e backup",
  "Limpeza e higienização",
];

const brands = [
  { name: "Apple Watch", models: "Series 3, 4, 5, 6, 7, 8, 9, SE, Ultra" },
  { name: "Garmin", models: "Fenix, Forerunner, Venu, Instinct, Epix, Enduro" },
  { name: "Samsung Galaxy Watch", models: "Galaxy Watch 4, 5, 6, Active, Classic" },
  { name: "Outros", models: "Amazfit, Huawei Watch, Fitbit" },
];

const SmartwatchPage = () => {
  return (
    <div data-testid="smartwatch-page">
      {/* Hero Section */}
      <section className="hero-bg min-h-[60vh] flex items-center pt-32 md:pt-40 pb-16">
        <div className="hero-content max-w-7xl mx-auto px-6 w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <Link to="/" className="text-[#00F0FF] text-sm font-mono uppercase tracking-widest hover:underline mb-4 inline-block">
              ← Voltar para Início
            </Link>
            
            <div className="inline-block mb-6">
              <span className="text-[#00F0FF] text-sm font-mono uppercase tracking-widest border border-[#00F0FF]/30 px-4 py-2 bg-[#00F0FF]/5">
                [ Assistência Técnica Smartwatch ]
              </span>
            </div>
            
            <h1 className="font-orbitron text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              Reparo de <span className="text-[#00F0FF] neon-text">Smartwatch</span>
              <br />Apple Watch & Garmin
            </h1>
            
            <p className="text-[#94A3B8] text-lg md:text-xl font-space leading-relaxed mb-8">
              Especialistas em conserto de Apple Watch e relógios Garmin. Troca de tela, bateria, 
              botões e sensores. Serviço diferenciado para atletas e usuários exigentes.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <motion.a
                href={`https://wa.me/${WHATSAPP_NUMBER}?text=Olá! Preciso de assistência técnica para meu smartwatch.`}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="whatsapp-btn justify-center text-base"
                data-testid="smartwatch-whatsapp-btn"
              >
                <Phone size={20} />
                Orçamento pelo WhatsApp
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Garmin Highlight */}
      <section className="section-dark py-16">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="cyber-card p-8 md:p-12 border-[#00F0FF]/30"
          >
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <span className="text-[#00F0FF] text-sm font-mono uppercase tracking-widest">
                  [ Especialidade ]
                </span>
                <h2 className="font-orbitron text-2xl md:text-3xl font-bold text-white mt-4 mb-4">
                  Especialistas em <span className="text-[#00F0FF]">Garmin</span>
                </h2>
                <p className="text-[#94A3B8] font-space leading-relaxed mb-6">
                  Somos referência no Rio de Janeiro em reparo de relógios Garmin. 
                  Atendemos modelos Fenix, Forerunner, Venu, Instinct, Epix e Enduro 
                  com peças de qualidade e garantia total.
                </p>
                <a
                  href={`https://wa.me/${WHATSAPP_NUMBER}?text=Olá! Preciso de reparo para meu relógio Garmin.`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cyber-btn inline-block"
                  data-testid="garmin-cta-btn"
                >
                  Orçar Reparo Garmin
                </a>
              </div>
              <div className="flex justify-center">
                <div className="w-48 h-48 border border-[#00F0FF]/30 flex items-center justify-center bg-[#00F0FF]/5">
                  <Watch size={80} className="text-[#00F0FF]" />
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services List */}
      <section className="section-darker py-20 grid-bg">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Services */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-orbitron text-2xl md:text-3xl font-bold text-white mb-8">
                Serviços de <span className="text-[#00F0FF]">Reparo</span>
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {services.map((service, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-center gap-3"
                  >
                    <CheckCircle size={18} className="text-[#00F0FF] flex-shrink-0" />
                    <span className="text-sm text-[#94A3B8] font-space">{service}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Brands */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-orbitron text-2xl md:text-3xl font-bold text-white mb-8">
                Marcas <span className="text-[#00F0FF]">Atendidas</span>
              </h2>
              <div className="space-y-4">
                {brands.map((brand, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="cyber-card p-6"
                  >
                    <div className="flex items-center gap-4">
                      <Watch size={24} className="text-[#00F0FF]" />
                      <div>
                        <h3 className="font-rajdhani font-bold text-white text-lg">{brand.name}</h3>
                        <p className="text-sm text-[#94A3B8]">{brand.models}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section-dark py-20">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="font-orbitron text-2xl md:text-3xl font-bold text-white text-center mb-12">
            Diferenciais do <span className="text-[#00F0FF]">Serviço</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Shield, title: "Garantia de 90 dias", desc: "Todos os reparos com garantia total em peças e serviços." },
              { icon: Clock, title: "Peças Originais", desc: "Trabalhamos com peças originais e de alta qualidade." },
              { icon: Award, title: "Especialistas Garmin", desc: "Experiência comprovada em relógios esportivos Garmin." },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="cyber-card p-8 text-center"
              >
                <div className="w-16 h-16 mx-auto mb-6 border border-[#00F0FF]/30 flex items-center justify-center bg-[#00F0FF]/5">
                  <item.icon size={28} className="text-[#00F0FF]" />
                </div>
                <h3 className="font-rajdhani font-bold text-white text-xl mb-3">{item.title}</h3>
                <p className="text-sm text-[#94A3B8] font-space">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <FAQ />
      <Contact />
    </div>
  );
};

export default SmartwatchPage;
